import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { path } from 'ramda'
import { calculateEvapotranspiration } from '../../common/ambient'
import FormattedDataPoint from './FormattedDataPoint'

export default function EvapoWidget({ currentDevice }) {
  const [etValue, setEtValue] = useState(null)
  const [error, setError] = useState(null)

  const { lastData } = currentDevice
  const coords = path(['info', 'coords', 'coords'], currentDevice)

  // Check required fields
  const hasRequiredData =
    lastData &&
    path(['hl', 'tempf', 'h'], lastData) &&
    path(['hl', 'tempf', 'l'], lastData) &&
    path(['hl', 'humidity', 'h'], lastData) &&
    path(['hl', 'humidity', 'l'], lastData) &&
    (lastData.solarradiation || path(['hl', 'solarradiation', 's'], lastData)) &&
    (lastData.windspeedmph || path(['hl', 'windspeedmph', 's'], lastData)) &&
    coords && coords.lat && coords.lon

  // Calculate averages (fallback to current values if hl data not available)
  const avgSolarRadiation = path(['hl', 'solarradiation', 's'], lastData) &&
                            path(['hl', 'solarradiation', 'c'], lastData)
    ? lastData.hl.solarradiation.s / lastData.hl.solarradiation.c
    : lastData.solarradiation

  const avgWindSpeed = path(['hl', 'windspeedmph', 's'], lastData) &&
                       path(['hl', 'windspeedmph', 'c'], lastData)
    ? lastData.hl.windspeedmph.s / lastData.hl.windspeedmph.c
    : lastData.windspeedmph

  // Get elevation from device info, fallback to sea level
  const elevation = path(['info', 'coords', 'elevation'], currentDevice) || 0

  useEffect(() => {
    if (!hasRequiredData) {
      setEtValue(null)
      setError(null)
      return
    }

    try {
      const result = calculateEvapotranspiration({
        maxTempF: lastData.hl.tempf.h,
        minTempF: lastData.hl.tempf.l,
        avgSolarRadiationW: avgSolarRadiation,
        avgWindSpeedMPH: avgWindSpeed,
        elevationMeters: elevation,
        maxHumidity: lastData.hl.humidity.h,
        minHumidity: lastData.hl.humidity.l,
        latitude: coords.lat,
        date: new Date()
      })

      if (result) {
        setEtValue(result)
        setError(null)
      } else {
        setError('Unable to calculate ET')
      }
    } catch (err) {
      console.error('ET calculation error:', err)
      setError('Calculation error')
    }
  }, [hasRequiredData, elevation, lastData, avgSolarRadiation, avgWindSpeed, coords])

  return (
    <div className="device-evapo-widget">
      {error && (
        <div className="evapo-error">{error}</div>
      )}

      {!error && !hasRequiredData && (
        <div className="evapo-error">
          ET requires temperature, humidity, wind, and solar radiation data.
        </div>
      )}

      {!error && hasRequiredData && etValue && (
        <div className="evapo-display center-aligned">
          <FormattedDataPoint
            className="et-value"
            type="dailyrainin"
            value={etValue.inches}
            after="/day"
          />
        </div>
      )}
    </div>
  )
}

EvapoWidget.propTypes = {
  currentDevice: PropTypes.object.isRequired
}

EvapoWidget.displayName = 'EvapoWidget'
